import { MapPinHouse, Star } from 'lucide-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import SafeImage from '../../../components/SafeImage/SafeImage';
import defaultBackground from '../../../img/defaultGraveCoverPhoto.jpg';
import { getRelationLabels } from '../../../pages/FamilyTree/utils/getRelationLabels';
import { protectedUrl, request } from '../../../service/request';
import { storeGraveInCache } from '../../../service/graveCache';
import './Grave.css';
import GraveButton from './GraveButton';
import PopoverModal from './PopoverModal';

const GraveResultCard = ({ grave, connectedGraves = [], style, onGraveUpdated = () => {} }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [openPhysicalPopover, setOpenPhysicalPopover] = React.useState(null);
  const [relations, setRelations] = React.useState([]);
  const open = Boolean(openPhysicalPopover);
  const [prefetched, setPrefetched] = React.useState(false);
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    if (!grave?.slug) return undefined;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No observer support; prefetch immediately once
      if (!prefetched) {
        request(`/graves/${grave.slug}`, {}, 'get').catch(() => {});
        setPrefetched(true);
      }
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !prefetched) {
            request(`/graves/${grave.slug}`, {}, 'get')
              .then((res) => {
                if (res?.data) storeGraveInCache(grave.slug, res.data);
              })
              .catch(() => {});
            setPrefetched(true);
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [grave?.slug, prefetched]);

  const handlePopoverClose = () => {
    setOpenPhysicalPopover(null);
  };

  const renderRelations = () => {
    if (!relations || relations.length === 0) return null;

    const onNavigateToUser = (slug) => {
      navigate(`/members/${slug}`);
    };

    return (
      <div className="relations">
        {relations.map((relation, index) => {
          const {
            primaryLabel,
            objectLabel,
            fallbackObjectLabel,
            subjectLabel,
            fallbackSubjectLabel,
          } = getRelationLabels(relation, intl);
          const relationLabel =
            primaryLabel ||
            objectLabel ||
            fallbackObjectLabel ||
            subjectLabel ||
            fallbackSubjectLabel;

          if (!relationLabel) return null;

          const key =
            relation?.slug ||
            relation?.id ||
            relation?.user_id ||
            relation?.user_slug ||
            relation?.user?.slug ||
            relation?.user?.id ||
            `relation-${index}`;

          return (
            <div key={key} className="relLine">
              <span className="relation-type">{relationLabel}</span>
              <span>
                <FormattedMessage id="relation_to" />
              </span>
              <span className="relation-user-name" onClick={() => onNavigateToUser(relation?.slug)}>
                {relation?.user_name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderConnectedGraves = () => {
    if (!connectedGraves || connectedGraves.length === 0) return null;
    const items = connectedGraves.slice(0, 3);
    const remaining = connectedGraves.length - items.length;

    return (
      <div className="connected-graves">
        {items.map((cg) => (
          <a
            key={cg.id}
            href={`/graves/${cg.slug || cg.id}`}
            className="connected-chip"
            aria-label={`View grave of ${cg.name}`}
          >
            <img
              src={cg.avatar ? protectedUrl(cg.avatar) : defaultBackground}
              alt=""
              loading="lazy"
            />
            <span className="connected-name">{cg.name}</span>
          </a>
        ))}
        {remaining > 0 && (
          <a
            href={`/search/graves?connected=${grave.id}`}
            className="connected-chip"
            aria-label={`View ${remaining} more connected graves`}
          >
            +{remaining}
          </a>
        )}
      </div>
    );
  };

  React.useEffect(() => {
    if (grave.grave_users && grave.grave_users.length > 0) {
      setRelations(grave.grave_users);
    }
  }, [grave.grave_users]);

  const gotoGrave = () => {
    navigate(`/graves/${grave.slug}`);
  };

  const convertDate = (data) => {
    try {
      if (!data) return '—';
      return `${data.slice(-2)}.${data.slice(-5, -3)}.${data.slice(-10, -6)}`;
    } catch {
      return String(data || '—');
    }
  };

  const born = convertDate(grave.date_of_birth);
  const died = convertDate(grave.date_of_death);
  const municipality = grave.municipality && grave.municipality.name ? grave.municipality.name : '';
  const graveyard = grave.graveyard && grave.graveyard.name ? grave.graveyard.name : '';
  const slugify = (value = '') =>
    value
      .normalize('NFD')
      .replace(/[æÆ]/g, 'ae')
      .replace(/[øØ]/g, 'o')
      .replace(/[åÅ]/g, 'a')
      .replace(/[äÄ]/g, 'a')
      .replace(/[öÖ]/g, 'o')
      .replace(/[üÜ]/g, 'u')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const municipalitySlug = grave?.municipality?.slug
    ? slugify(grave.municipality.slug)
    : grave?.municipality?.name
      ? slugify(grave.municipality.name)
      : null;
  const graveyardSlug = grave?.graveyard?.slug
    ? slugify(grave.graveyard.slug)
    : grave?.graveyard?.name
      ? slugify(grave.graveyard.name)
      : null;
  const cemeteryHref =
    municipalitySlug && graveyardSlug
      ? `/finn-en-grav/${municipalitySlug}/${graveyardSlug}`
      : graveyardSlug
        ? `/cemeteries/${graveyardSlug}`
        : `/cemeteries?name=${encodeURIComponent(graveyard)}`;
  const coverSrc = grave?.cover_image ? grave?.cover_image.file_url : null;

  return (
    <div className="grave grave--card" style={style} ref={cardRef}>
      {open && (
        <PopoverModal
          open={open}
          grave={grave}
          openPhysicalPopover={openPhysicalPopover}
          handlePopoverClose={handlePopoverClose}
        />
      )}
      <div
        className="grave-cardImage grave-cover"
        onClick={gotoGrave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' ? gotoGrave() : null)}
      >
        <SafeImage
          src={coverSrc}
          alt={grave.name}
          style={{ background: 'var(--sky-3, #bae6fd)' }}
        />
        <div className="grave-imageGradient" aria-hidden />
        <div className="grave-dateBadge">
          {born} <span className="sep">–</span> {died}
        </div>
      </div>
      <div className="grave-cardBody">
        <div className="flex items-center justify-between">
          <div
            className="grave-title line-clamp-2"
            onClick={gotoGrave}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' ? gotoGrave() : null)}
          >
            {grave.name}
          </div>
        </div>
        <div className="grave-meta">
          <div className="grave-metaRow">
            <span className="grave-metaIcon" aria-hidden>
              <Star size={12} />
            </span>
            <span>{born}</span>
            <span className="sep">–</span>
            <span>{died}</span>
          </div>
          <div className="grave-metaRow">
            <span className="grave-metaIcon" aria-hidden>
              <MapPinHouse size={12} style={{ color: 'var(--text-secondary)' }} />
            </span>
            <span>{municipality}</span>
            <span className="sep">–</span>
            {graveyard ? (
              <a
                className="grave-metaPlace grave-cemeteryLink"
                href={cemeteryHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.stopPropagation();
                }}
                title={graveyard}
              >
                {graveyard}
              </a>
            ) : (
              <span className="grave-metaPlace">—</span>
            )}
          </div>
        </div>
        {renderRelations()}
        {renderConnectedGraves()}
        <div className="grave-ctaRow">
          <GraveButton grave={grave} onGraveUpdated={onGraveUpdated} />
        </div>
      </div>
    </div>
  );
};

export default GraveResultCard;
